import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { getRoundRobinBaseUrl } from '../../../loadBalancer';
// Create an authentication context
const AuthContext = createContext();
let channel = null; // Singleton instance for BroadcastChannel

const token = localStorage.getItem('access_token');
export const AuthProvider = ({ children }) => {
  // Define authentication state
  const [authState, setAuthState] = useState({
    isAuthenticated: !!token,
    accessToken: localStorage.getItem('access_token') || null,
    email: '',
    role: localStorage.getItem('role'),
    client: localStorage.getItem('client'),
    scope: localStorage.getItem('scope'),
    employeeID: localStorage.getItem('employeeID'),
    username: localStorage.getItem('username'),
    name: localStorage.getItem('name'),
    endClient: localStorage.getItem('endClient'),
    sessionId: localStorage.getItem('sessionId'),
  });

  // Unique session key to track the active tab
  const SESSION_KEY = 'activeTabId';
  const tabId = useRef(Date.now().toString()); // Generate unique tab ID

  const excludedPaths = ['/ClientMgmt', '/ClientMgmt/ClientView'];
  const isExcludedPath = (path = window.location.pathname) => {
    return excludedPaths.includes(path);
  };

  // Initialize BroadcastChannel if not already created
  if (!channel) {
    channel = new BroadcastChannel('tab_control');
  }

  /**
   * Handles user login and stores authentication details in localStorage
   */
  const login = (accessToken, email, role, client, scope, employeeID, username, name, endClient, sessionId) => {
    // Store authentication details in localStorage
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('role', role);
    localStorage.setItem('client', client);
    localStorage.setItem('scope', scope);
    localStorage.setItem('employeeID', employeeID);
    localStorage.setItem('username', username);
    localStorage.setItem('name', name);
    localStorage.setItem('endClient', endClient);
    if (sessionId) {
      localStorage.setItem('sessionId', sessionId);
    }

    // Update authentication state
    setAuthState({
      isAuthenticated: true,
      accessToken,
      email,
      role,
      client,
      scope,
      employeeID,
      username,
      name,
      endClient,
      sessionId,
    });

    // Apply tab restriction only for employees
    if (role === 'employee') {
      localStorage.setItem(SESSION_KEY, tabId.current);
      if (channel) {
        channel.postMessage({ type: 'newTab', tabId: tabId.current });
      }
    }
  };

  /**
   * Handles user logout and clears authentication data
   */
  const logout = async () => {
    // Format logout time in Asia/Kolkata timezone
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    const parts = formatter.formatToParts(new Date());
    const datePart = `${parts[0].value}/${parts[2].value}/${parts[4].value}`;
    const timePart = `${parts[6].value}:${parts[8].value}`;
    // const logoutTime = `${datePart}:${timePart}`;
    // const userId = localStorage.getItem("employeeID");

    // Log logout time to the backend
    try {
      let baseUrls = getRoundRobinBaseUrl();
      const sessionId = authState.sessionId;
      await fetch(`${baseUrls}/uploadedFile/createLogOutTime?sessionId=${sessionId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        // body: JSON.stringify({ logoutTime, userId }),
      });
    } catch (error) {
      console.error('Failed to log logout time:', error);
    }

    try {
      // STEP 1: Get base URL and token (fresh, in this scope)
      const baseUrls = getRoundRobinBaseUrl();

      // STEP 2: Call the inactivate endpoint
      const inactivateRes = await fetch(`${baseUrls}/LoginStatus/inactivate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        },
        // STEP 3: (Optional) send context if your API accepts it
        // body: JSON.stringify({
        //   employeeID: localStorage.getItem('employeeID'),
        //   sessionId: authState.sessionId,
        // }),
      });

      if (!inactivateRes.ok) {
        const msg = await inactivateRes.text().catch(() => inactivateRes.statusText);
        console.warn('LoginStatus/inactivate failed:', msg);
      } else {
        console.log('LoginStatus marked inactive successfully');
      }
    } catch (err) {
      console.error('Error calling LoginStatus/inactivate:', err);
    }

    // Clear authentication data from localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('role');
    localStorage.removeItem('client');
    localStorage.removeItem('scope');
    localStorage.removeItem('employeeID');
    localStorage.removeItem('username');
    localStorage.removeItem('name');
    localStorage.removeItem('endClient');
    localStorage.removeItem('sessionId');
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem('sessionId');

    // Reset authentication state
    setAuthState({
      isAuthenticated: false,
      accessToken: null,
      email: '',
      role: '',
      client: '',
      scope: '',
      employeeID: '',
      username: '',
      name: '',
      endClient: '',
      sessionId: null,
    });

    // Notify other tabs to logout
    if (channel) {
      channel.postMessage({ type: 'logout' });
    }
    window.history.pushState(null, null, '/login');

    setTimeout(() => {
      window.location.href = '/login';
    }, 100);
  };

  /**
   * Checks if the access token is expired
   */
  const isTokenExpired = (token) => {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.exp * 1000 < Date.now();
    } catch (error) {
      console.error('Invalid token format', error);
      return true;
    }
  };

  /**
   * Handles tab control and session management for employees
   */
  useEffect(() => {
    if (!channel) {
      channel = new BroadcastChannel('tab_control');
    }

    // const excludedPaths = ['/ClientMgmt/ClientView', '/ClientMgmt']; // Add paths where multiple tabs are allowed
    // const isExcludedPath = () => {
    //   return excludedPaths.some((path) => window.location.pathname.startsWith(path));
    // };

    const checkTabStatus = () => {
      const activeTab = localStorage.getItem(SESSION_KEY);

      // Only consider non-excluded paths for tab restriction
      if (authState.role === 'employee') {
        if (!isExcludedPath()) {
          if (!activeTab) {
            // No active tab stored → set this tab as active
            localStorage.setItem(SESSION_KEY, tabId.current);
          } else if (activeTab !== tabId.current) {
            // Another active tab exists → close this tab
            if (channel) {
              channel.postMessage({ type: 'closeTab', tabId: tabId.current });
            }
            setTimeout(() => window.close(), 500);
          }
        }
      }
    };

    const handleUnload = () => {
      if (authState.role === 'employee' && !isExcludedPath() && localStorage.getItem(SESSION_KEY) === tabId.current) {
        localStorage.removeItem(SESSION_KEY);
      }
    };

    // Handle incoming messages from other tabs
    if (authState.role === 'employee' && channel) {
      channel.onmessage = (event) => {
        if (event.data.type === 'closeTab') {
          if (!isExcludedPath() && event.data.tabId !== tabId.current) {
            setTimeout(() => window.close(), 500);
          }
        } else if (event.data.type === 'logout') {
          logout();
        }
      };
    }

    if (authState.role === 'employee') {
      checkTabStatus();
      window.addEventListener('beforeunload', handleUnload);
    }

    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      if (channel) {
        channel.close();
        channel = null;
      }
    };
  }, [authState.role]);

  /**
   * Automatically logs out the user if the access token expires
   */
  useEffect(() => {
    if (authState.accessToken && isTokenExpired(authState.accessToken)) {
      logout();
    }
  }, [authState.accessToken]);

  //   // Force non-excluded paths to open in new window for employees
  // useEffect(() => {
  //   if (authState.role === 'employee') {
  //     const handleClick = (e) => {
  //       const link = e.target.closest('a');
  //       if (!link) return;

  //       const href = new URL(link.href, window.location.origin).pathname;

  //       // Only intercept same-origin links
  //       if (href.startsWith('/')) {
  //         if (!isExcludedPath(href)) {
  //           e.preventDefault();
  //           window.open(href, '_blank'); // open in new tab/window
  //         }
  //       }
  //     };

  //     document.addEventListener('click', handleClick);
  //     return () => document.removeEventListener('click', handleClick);
  //   }
  // }, [authState.role]);

  // useEffect(() => {
  //   if (authState.role === 'employee') {
  //     const handleClick = (e) => {
  //       const link = e.target.closest('a');
  //       if (!link) return;

  //       const href = new URL(link.href, window.location.origin).pathname;

  //       const activeTab = localStorage.getItem(SESSION_KEY);
  //       const isCurrentTabActive = activeTab === tabId.current;

  //       // ✅ Only apply force-new-window if:
  //       // - this tab is NOT the active tab
  //       // - path is NOT excluded
  //       if (!isExcludedPath(href) && !isCurrentTabActive) {
  //         e.preventDefault();
  //         window.open(href, '_blank');
  //         return;
  //       }

  //       // ✅ If this tab IS active tab and path is excluded → allow normal navigation
  //       if (isCurrentTabActive) {
  //         return; // do nothing, allow navigation inside first tab
  //       }
  //     };

  //     document.addEventListener('click', handleClick);
  //     return () => document.removeEventListener('click', handleClick);
  //   }
  // }, [authState.role]);

  useEffect(() => {
    if (authState.role === 'employee') {
      const handleClick = (e) => {
        const link = e.target.closest('a');
        if (!link) return;

        const href = new URL(link.href, window.location.origin).pathname;

        const activeTab = localStorage.getItem(SESSION_KEY);
        const isCurrentTabActive = activeTab === tabId.current;

        // 🚫 Block navigation completely for non-excluded paths in non-active tabs
        if (!isExcludedPath(href) && !isCurrentTabActive) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation(); // 🔑 ensures React Router's handler never fires

          // Open in new tab
          window.open(href, '_blank');

          // Close this tab quickly (without navigating)
          setTimeout(() => window.close(), 50);
        }
      };

      // Attach with capture = true so we intercept before React Router
      document.addEventListener('click', handleClick, true);

      return () => document.removeEventListener('click', handleClick, true);
    }
  }, [authState.role]);

  return <AuthContext.Provider value={{ ...authState, login, logout }}>{children}</AuthContext.Provider>;
};

// Custom hook to use authentication context
export const useAuth = () => useContext(AuthContext);
